/**
 * @file useSenderAddressResolver.ts
 * @description Разбор адреса склада в поля отправления (phase19.12).
 *
 * Один путь для всех источников адреса (подсказка DaData, кнопка «Подтянуть из
 * склада»): адрес → город и индекс из подсказки → код населённого пункта из
 * справочника перевозчика. Менеджер не должен знать, что такое «код СДЭК».
 */

import { useCallback, useState } from "react";

import { searchCarrierCities, type CarrierKind } from "../../lib/api-shop-carriers";
import type { SenderFormValue } from "../../lib/carrier-sender";
import { addresses } from "../../lib/api-customers";
import {
  cityFromAddressLine,
  suggestionCityName,
  type DadataAddrSuggestion,
} from "../../lib/dadata-address";

interface Options {
  carrier: CarrierKind;
  onChange: (patch: Partial<SenderFormValue>) => void;
  /** Сохранённый адрес: из него достаём город до первого поиска. */
  initialAddress?: string;
}

export interface SenderAddressResolver {
  /** Город, который распознали из адреса (для строки-итога). */
  cityName: string | null;
  setCityName: (value: string | null) => void;
  /** Предупреждение: город нашли неточно или не нашли вовсе. */
  hint: string | null;
  resolving: boolean;
  /** Подсказка DaData выбрана менеджером. */
  applySuggestion: (suggestion: DadataAddrSuggestion) => Promise<void>;
  /** Готовая строка адреса (склад): сначала уточняем её в DaData. */
  applyRawAddress: (address: string) => Promise<void>;
}

export function useSenderAddressResolver({
  carrier,
  onChange,
  initialAddress,
}: Options): SenderAddressResolver {
  const [cityName, setCityName] = useState<string | null>(() =>
    cityFromAddressLine(initialAddress ?? ""),
  );
  const [hint, setHint] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  /** Название города → код населённого пункта в справочнике перевозчика. */
  const resolveCityCode = useCallback(
    async (city: string): Promise<void> => {
      try {
        const found = await searchCarrierCities(carrier, city);
        const exact = found.find(
          (c) => c.city.trim().toLowerCase() === city.trim().toLowerCase(),
        );
        const chosen = exact ?? found[0];
        if (!chosen) {
          setHint(`«${city}» нет в справочнике перевозчика — введите код города вручную.`);
          return;
        }
        onChange({ senderCityCode: String(chosen.code) });
        setHint(
          exact
            ? null
            : `Проверьте: найден ${chosen.city}${chosen.region ? ` (${chosen.region})` : ""}.`,
        );
      } catch {
        setHint("Справочник городов перевозчика недоступен — введите код вручную.");
      }
    },
    [carrier, onChange],
  );

  const applySuggestion = useCallback(
    async (suggestion: DadataAddrSuggestion): Promise<void> => {
      const city = suggestionCityName(suggestion);
      onChange({
        senderAddress: suggestion.value,
        senderPostalCode: suggestion.data.postal_code ?? "",
      });
      setCityName(city);
      setHint(null);
      if (!city) {
        setHint("В адресе не нашёлся город — уточните адрес или введите код вручную.");
        return;
      }
      setResolving(true);
      try {
        await resolveCityCode(city);
      } finally {
        setResolving(false);
      }
    },
    [onChange, resolveCityCode],
  );

  const applyRawAddress = useCallback(
    async (address: string): Promise<void> => {
      const trimmed = address.trim();
      if (!trimmed) return;
      onChange({ senderAddress: trimmed });
      setResolving(true);
      try {
        const res = await addresses.suggest(trimmed, 1);
        const first = (res.suggestions as DadataAddrSuggestion[])[0];
        if (!first) {
          setCityName(null);
          setHint("Город определить не удалось — уточните поиском.");
          return;
        }
        await applySuggestion(first);
      } catch {
        setCityName(null);
        setHint("Город определить не удалось — уточните поиском.");
      } finally {
        setResolving(false);
      }
    },
    [applySuggestion, onChange],
  );

  return {
    cityName,
    setCityName,
    hint,
    resolving,
    applySuggestion,
    applyRawAddress,
  };
}
